import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * MOCK ASSETS
 */
const landscape1 = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000";
const landscape2 = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000";
const landscape3 = "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&q=80&w=2000";

const slides = [
  {
    image: landscape1,
    title: 'Empowering Business Growth Through Innovative Technologies',
    subtitle:
      "At BytesFarms, we don't just build software — we craft scalable tech ecosystems that empower businesses to thrive. From startups to enterprises, our solutions are designed to grow with you, backed by a team obsessed with innovation and impact.",
  },
  {
    image: landscape2,
    title: 'All-in-One. Reliable. Scalable. Future-Ready',
    subtitle:
      "BytesFarms takes the complexity out of digital transformation. We manage everything — websites, mobile apps, DevOps, cloud infrastructure, analytics, and more — so you don't need ten vendors, just one powerhouse team aligned with your mission.",
  },
  {
    image: landscape3,
    title: 'Excellence in IT Services That Drive Real-World Impact',
    subtitle:
      "We combine cutting-edge technology with deep domain knowledge to deliver results that matter. At BytesFarms, our IT services are crafted for performance, security, and long-term value — with transparency, agility, and your success at the core.",
  },
];

/**
 * Custom Animated Button Component
 */
const CTAButton = ({ text, hoverText, onClick, variant = 'primary' }) => {
  return (
    <>
      <style>{`
        .custom-btn {
          position: relative;
          overflow: hidden;
          border: none;
          color: #fff;
          font-size: 15px;
          line-height: 15px;
          padding: 14px 28px;
          border-radius: 999px;
          text-decoration: none;
          cursor: pointer;
          user-select: none;
          transition: all 0.3s ease-in-out;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 180px;
          z-index: 1; /* Ensure button stays above the :after layer */
        }

        .custom-btn.primary { background: #F7941D; }
        .custom-btn.secondary {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(4px);
        }

        .custom-btn span:first-child {
          position: relative;
          transition: color 600ms cubic-bezier(0.48, 0, 0.12, 1);
          z-index: 10;
        }

        .custom-btn span:last-child {
          /* Changed hover text color to black to be visible on white BG */
          color: #000000; 
          display: block;
          position: absolute;
          bottom: 0;
          transition: all 500ms cubic-bezier(0.48, 0, 0.12, 1);
          z-index: 100;
          opacity: 0;
          top: 50%;
          left: 50%;
          transform: translateY(225%) translateX(-50%);
          height: 15px;
          line-height: 15px;
          white-space: nowrap;
          font-weight: 600;
        }

        .custom-btn:after {
          content: "";
          position: absolute;
          bottom: -50%;
          left: 0;
          width: 100%;
          height: 100%;
          transform-origin: bottom center;
          transition: transform 600ms cubic-bezier(0.48, 0, 0.12, 1);
          transform: skewY(9.3deg) scaleY(0);
          z-index: 50;
          background-color: #ffffff; /* Unified both to white */
        }

        /* Removed specific black/blue backgrounds here */
        .custom-btn:hover:after { 
          transform: skewY(9.3deg) scaleY(2.5); 
        }

        .custom-btn:hover span:last-child {
          transform: translateX(-50%) translateY(-50%);
          opacity: 1;
          transition: all 900ms cubic-bezier(0.48, 0, 0.12, 1);
        }
        
        /* Optional: Makes the original text disappear more smoothly on hover */
        .custom-btn:hover span:first-child {
          opacity: 0;
        }
      `}</style>
      <button className={`custom-btn ${variant}`} onClick={onClick}>
        <span>{text}</span>
        <span>{hoverText}</span>
      </button>
    </>
  );
};

export default function App() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoPlayRef = useRef();

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
      setIsTransitioning(false);
    }, 600);
  }, [isTransitioning]);

  useEffect(() => {
    autoPlayRef.current = nextSlide;
  });

  useEffect(() => {
    const play = () => autoPlayRef.current();
    const interval = setInterval(play, 6000);
    return () => clearInterval(interval);
  }, []);

const navigate = useNavigate();

const handleTalkToExpert = () => { navigate('/ContactUs'); };
const handleViewServices = () => { navigate('/AboutUs/Company'); };

  return (
    <section id="home" className="relative w-full h-screen overflow-hidden bg-slate-900 font-sans mt-[30px]" style={{ isolation: 'isolate' }}>
      
      {/* Background */}
      <div className="absolute inset-0 w-full h-full">
        {slides.map((slide, index) => (
          <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === activeIndex ? 'opacity-100 z-0' : 'opacity-0 z-[-1]'}`}>
            <img src={slide.image} alt={`Slide ${index}`} className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-linear ${index === activeIndex ? 'scale-110' : 'scale-100'}`} />
          </div>
        ))}
      </div>

      <div className="absolute inset-0 z-10 pointer-events-none" style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)' }} />

      {/* Content Section — added py-20 md:py-32 for top/bottom breathing room */}
      <div className="absolute inset-0 z-20 flex items-center justify-center text-center px-4 md:px-6">
        <div className="max-w-3xl w-full text-white">
          <div
            className={`
              space-y-4 md:space-y-6
              py-32 md:py-48
              transition-all duration-700 ease-out transform
              ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}
            `}
          >
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight tracking-tight">
              {slides[activeIndex].title}
            </h1>

            <p className="text-base md:text-lg text-slate-200 max-w-xl mx-auto leading-relaxed opacity-90">
              {slides[activeIndex].subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <CTAButton text="Talk to an Expert" hoverText="Let's Connect" onClick={handleTalkToExpert} variant="primary" />
              <CTAButton text="View Our Services" hoverText="Explore More" onClick={handleViewServices} variant="secondary" />
            </div>
          </div>
        </div>
      </div>

      {/* Dot Navigation */}
      <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
        {slides.map((_, i) => (
          <button key={i} onClick={() => setActiveIndex(i)} className="group relative flex items-center justify-center p-1.5" aria-label={`Go to slide ${i + 1}`}>
            <div className={`transition-all duration-500 rounded-full ${i === activeIndex ? 'w-8 h-1.5' : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/60'}`} 
                 style={{ backgroundColor: i === activeIndex ? '#F7941D' : undefined }} />
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="absolute bottom-0 left-0 w-full z-30 pointer-events-none" style={{ height: 160 }}>
        <svg viewBox="0 0 1440 180" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
          <path d="M0,90 C240,180 480,20 720,90 C960,160 1200,30 1440,90 L1440,180 L0,180 Z" fill="rgba(255,255,255,0.08)" />
          <path d="M0,120 C300,40 600,170 900,100 C1100,55 1280,140 1440,110 L1440,180 L0,180 Z" fill="rgba(255,255,255,0.15)" />
          <path d="M0,145 C200,80 500,180 800,130 C1050,88 1260,160 1440,125 L1440,180 L0,180 Z" fill="#ffffff" />
        </svg>
      </div>
    </section>
  );
}